import React, { useEffect, useRef, useState, useCallback } from "react";
import { FaMicrophone, FaTimes } from "react-icons/fa";
import { edgeVoiceMap, chromeVoiceMap, safariVoiceMap } from "./voiceConfig";

const BACKEND_URL = "https://staging.parkengage.ai/api";

const Orb = ({ onClose }) => {
  const recognitionRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const abortRef = useRef(null);        // <-- NEW
  const isClosingRef = useRef(false);   // <-- NEW

  const [listening, setListening] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const [input, setInput] = useState("");
  const [closing, setClosing] = useState(false); // <-- NEW (loading screen)

  const debug = (msg) => console.log("📤 SENT →", msg);

  // -------------------------------------------------
  // BACKEND QUERY (with abort support)
  // -------------------------------------------------
  const handleQuery = useCallback(
    async (question) => {
      if (!question || isClosingRef.current) return;

      debug(question);

      try {
        abortRef.current = new AbortController();

        const res = await fetch(`${BACKEND_URL}/query`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: abortRef.current.signal,
          body: JSON.stringify({ question, session_id: sessionId }),
        });

        if (isClosingRef.current) return;

        const data = await res.json();
        const lang = data.lang || "en-US";

        await speak(data.text || "Sorry, I couldn't understand.", lang);
      } catch (err) {
        if (err.name === "AbortError") return; // cancelled
        console.error("❌ Backend error:", err);
      }
    },
    [sessionId]
  );

  // -------------------------------------------------
  // TTS
  // -------------------------------------------------
  const speak = async (text, lang) => {
    if (!text || !window.speechSynthesis || isClosingRef.current) return;

    try {
      recognitionRef.current?.stop();
    } catch {}
    setListening(false);

    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = lang;
    utter.pitch = 1.05;
    utter.rate = 1.0;

    const ua = navigator.userAgent.toLowerCase();
    const isEdge = ua.includes("edg");
    const isChrome = ua.includes("chrome") && !ua.includes("edg");
    const isSafari = ua.includes("safari") && !ua.includes("chrome");

    const voiceMap =
      (isSafari && safariVoiceMap[lang]) ||
      (isEdge && edgeVoiceMap[lang]) ||
      (isChrome && chromeVoiceMap[lang]) ||
      null;

    const assignVoiceAndSpeak = () => {
      if (isClosingRef.current) return;

      const voices = window.speechSynthesis.getVoices();
      let chosen =
        (voiceMap && voices.find((v) => voiceMap.test(v.name))) ||
        voices.find((v) => v.lang === lang) ||
        voices[0];

      utter.voice = chosen;

      utter.onend = () => {
        if (isClosingRef.current) return;
        try {
          recognitionRef.current?.start();
          setListening(true);
        } catch {}
      };

      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utter);
    };

    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) {
      window.speechSynthesis.onvoiceschanged = assignVoiceAndSpeak;
    } else {
      assignVoiceAndSpeak();
    }
  };

  // -------------------------------------------------
  // STT
  // -------------------------------------------------
  const initSTT = useCallback(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.error("❌ Web Speech STT not supported.");
      return;
    }

    const rec = new SpeechRecognition();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = "en-US";

    rec.onresult = async (event) => {
      if (isClosingRef.current) return;

      const text =
        event.results[event.results.length - 1][0].transcript.trim();

      console.log("🗣 USER SAID:", text);

      setInput(text);
      await handleQuery(text);
    };

    rec.onend = () => {
      if (listening && !isClosingRef.current) rec.start();
    };

    recognitionRef.current = rec;
  }, [handleQuery, listening]);

  useEffect(() => {
    initSTT();
  }, [initSTT]);

  // -------------------------------------------------
  // MIC TOGGLE
  // -------------------------------------------------
  const handleMicToggle = () => {
    if (isClosingRef.current) return;

    if (!audioUnlockedRef.current) {
      const unlock = new SpeechSynthesisUtterance("");
      window.speechSynthesis.speak(unlock);
      audioUnlockedRef.current = true;
      console.log("🔓 iOS audio unlocked");
    }

    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
    } else {
      try {
        recognitionRef.current?.start();
        setListening(true);
      } catch (err) {
        console.error("❌ Failed to start STT:", err);
      }
    }
  };

  // -------------------------------------------------
  // CLOSE (with forced shutdown + loading)
  // -------------------------------------------------
  const handleClose = async () => {
    isClosingRef.current = true;
    setClosing(true);

    try {
      abortRef.current?.abort();     // stop backend call immediately
      recognitionRef.current?.stop();
      window.speechSynthesis.cancel();
    } catch {}

    setListening(false);

    // Delay to show loading animation
    await new Promise((res) => setTimeout(res, 500));

    onClose();
  };

  // -------------------------------------------------
  // UI
  // -------------------------------------------------
  if (closing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white text-xl">
        Closing...
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white">
      <div
        className={`w-48 h-48 rounded-full transition-all ${
          listening ? "animate-pulse scale-105" : "scale-100"
        }`}
        style={{
          background: "linear-gradient(135deg, #5CB6FF 0%, #B18CFF 100%)",
        }}
      ></div>

      <div className="flex items-center justify-center gap-6 mt-8">
        <button
          onClick={handleMicToggle}
          className={`w-14 h-14 flex items-center justify-center rounded-full text-white text-2xl ${
            listening ? "bg-red-500 animate-pulse" : "bg-gray-400"
          }`}
        >
          <FaMicrophone />
        </button>

        <button
          onClick={handleClose}
          className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-900 text-white text-2xl"
        >
          <FaTimes />
        </button>
      </div>
    </div>
  );
};

export default Orb;
